import { useState } from "react";
import { useRequest } from "./useRequest";

type UploadedDocumentEndpoint =
  | "pdf-proposal-adm"
  | "pdf-tally-adm"
  | "pdf-pulling-adm"
  | "additional-document-download";

export const useUploadedDocumentDownload = (endpoint: UploadedDocumentEndpoint) => {
  const { handleRequest } = useRequest();
  const [isDownloadingUploadedDocument, setIsDownloadingUploadedDocument] =
    useState(false);

  const downloadUploadedDocument = async (
    path: string,
    name: string,
    mimeType = "application/pdf;charset=utf-8",
  ) => {
    if (!path) return;

    setIsDownloadingUploadedDocument(true);
    try {
      const response = await handleRequest({
        endpoint,
        options: {
          method: "POST",
          body: JSON.stringify({ key: path }),
          headers: { responseType: "Buffer" } as any,
        },
      });

      if (!response.success) throw new Error("Unable to download document");

      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(response.data.data).buffer], {
          type: response.mimeType || mimeType,
        }),
      );
      const resolvedMimeType = response.mimeType || mimeType;
      const hasExtension = /\.[A-Za-z0-9]+$/.test(name);
      const downloadName =
        resolvedMimeType.includes("pdf") && !hasExtension ? `${name}.pdf` : name;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingUploadedDocument(false);
    }
  };

  return {
    downloadUploadedDocument,
    isDownloadingUploadedDocument,
  };
};
